/**
 * Journey config validation schemas.
 * Defines user journey steps and question types for comparison flows.
 */
import { z } from "zod";
import { EligibilityConditionSchema } from "./eligibility";
// ============================================================================
// Question Types
// ============================================================================
/** Question types supported in journey forms */
export const QuestionTypeSchema = z.enum([
    "single-select",
    "multi-select",
    "input",
    "slider",
    "date",
    "number",
    "text",
    "boolean",
]);
// ============================================================================
// Question Options
// ============================================================================
/** Option for select-type questions */
export const QuestionOptionSchema = z
    .object({
    value: z.string().min(1),
    label: z.string().min(1).max(100),
    description: z.string().max(255).optional(),
    icon: z.string().optional(),
})
    .strict();
// ============================================================================
// Question Configuration
// ============================================================================
/** Base question configuration */
const BaseQuestionConfigSchema = z.object({
    label: z.string().min(1).max(200),
    description: z.string().max(500).optional(),
    helpText: z.string().max(500).optional(),
    required: z.boolean(),
    placeholder: z.string().max(100).optional(),
});
/** Single-select question configuration */
export const SingleSelectQuestionConfigSchema = BaseQuestionConfigSchema.extend({
    type: z.literal("single-select"),
    options: z.array(QuestionOptionSchema).min(1),
    defaultValue: z.string().optional(),
}).strict();
/** Multi-select question configuration */
export const MultiSelectQuestionConfigSchema = BaseQuestionConfigSchema.extend({
    type: z.literal("multi-select"),
    options: z.array(QuestionOptionSchema).min(1),
    minSelections: z.number().int().min(0).optional(),
    maxSelections: z.number().int().min(1).optional(),
    defaultValue: z.array(z.string()).optional(),
}).strict();
/** Text input question configuration */
export const InputQuestionConfigSchema = BaseQuestionConfigSchema.extend({
    type: z.literal("input"),
    inputType: z.enum(["text", "email", "phone", "url"]).optional(),
    minLength: z.number().int().min(0).optional(),
    maxLength: z.number().int().min(1).optional(),
    pattern: z.string().optional(),
    defaultValue: z.string().optional(),
}).strict();
/** Slider question configuration */
export const SliderQuestionConfigSchema = BaseQuestionConfigSchema.extend({
    type: z.literal("slider"),
    min: z.number(),
    max: z.number(),
    step: z.number().positive().optional(),
    unit: z.string().max(20).optional(),
    showValue: z.boolean().optional(),
    defaultValue: z.number().optional(),
}).strict();
/** Date question configuration */
export const DateQuestionConfigSchema = BaseQuestionConfigSchema.extend({
    type: z.literal("date"),
    minDate: z.string().datetime().optional(),
    maxDate: z.string().datetime().optional(),
    format: z.enum(["date", "datetime", "time"]).optional(),
    defaultValue: z.string().optional(),
}).strict();
/** Number question configuration */
export const NumberQuestionConfigSchema = BaseQuestionConfigSchema.extend({
    type: z.literal("number"),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().positive().optional(),
    unit: z.string().max(20).optional(),
    prefix: z.string().max(10).optional(),
    suffix: z.string().max(10).optional(),
    defaultValue: z.number().optional(),
}).strict();
/** Text (multiline) question configuration */
export const TextQuestionConfigSchema = BaseQuestionConfigSchema.extend({
    type: z.literal("text"),
    minLength: z.number().int().min(0).optional(),
    maxLength: z.number().int().min(1).optional(),
    rows: z.number().int().min(1).max(20).optional(),
    defaultValue: z.string().optional(),
}).strict();
/** Boolean question configuration */
export const BooleanQuestionConfigSchema = BaseQuestionConfigSchema.extend({
    type: z.literal("boolean"),
    trueLabel: z.string().max(50).optional(),
    falseLabel: z.string().max(50).optional(),
    defaultValue: z.boolean().optional(),
}).strict();
/** Union of all question configurations */
export const QuestionConfigSchema = z.discriminatedUnion("type", [
    SingleSelectQuestionConfigSchema,
    MultiSelectQuestionConfigSchema,
    InputQuestionConfigSchema,
    SliderQuestionConfigSchema,
    DateQuestionConfigSchema,
    NumberQuestionConfigSchema,
    TextQuestionConfigSchema,
    BooleanQuestionConfigSchema,
]);
// ============================================================================
// Journey Step Types
// ============================================================================
/** Journey step types */
export const JourneyStepTypeSchema = z.enum(["form", "results", "details", "click_out"]);
// ============================================================================
// Step Configurations
// ============================================================================
/** Form field definition */
export const FormFieldSchema = z
    .object({
    key: z
        .string()
        .min(1)
        .regex(/^[a-z][a-zA-Z0-9]*$/, "Key must be camelCase"),
    question: QuestionConfigSchema,
    visibilityCondition: EligibilityConditionSchema.optional(),
})
    .strict();
/** Form step configuration (base schema for discriminated union) */
export const FormStepConfigBaseSchema = z
    .object({
    type: z.literal("form"),
    title: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    /** Reference to a schema definition for dynamic form generation */
    schemaRef: z.string().min(1).optional(),
    /** Inline field definitions (used if schemaRef not provided) */
    fields: z.array(FormFieldSchema).optional(),
    submitLabel: z.string().min(1).max(50),
    skipLabel: z.string().max(50).optional(),
    allowSkip: z.boolean().optional(),
})
    .strict();
/** Form step configuration with validation refinement */
export const FormStepConfigSchema = FormStepConfigBaseSchema.refine((data) => {
    // Must have either schemaRef or fields defined
    return data.schemaRef !== undefined || (data.fields !== undefined && data.fields.length > 0);
}, {
    message: "Form step must have either schemaRef or fields defined",
    path: ["fields"],
});
/** Comparison strategy configuration */
export const ComparisonStrategyConfigSchema = z
    .object({
    /** Strategy ID to use */
    strategyId: z.string().min(1),
    /** Display name for the strategy */
    displayName: z.string().min(1).max(100),
    /** Optional description for UI */
    description: z.string().max(255).optional(),
})
    .strict();
/** Results step configuration (Comparison Step) */
export const ResultsStepConfigSchema = z
    .object({
    type: z.literal("results"),
    title: z.string().min(1).max(100),
    showStrategySelector: z.boolean(),
    defaultStrategy: z.string().min(1),
    /** Available comparison strategies for the user to choose from */
    availableStrategies: z.array(ComparisonStrategyConfigSchema).optional(),
    resultsPerPage: z.number().int().min(1).max(100),
    showRankExplanation: z.boolean(),
    emptyStateMessage: z.string().max(255).optional(),
    filterOptions: z.array(z.string()).optional(),
    sortOptions: z.array(z.string()).optional(),
    /** Minimum products required to show results */
    minProductsRequired: z.number().int().min(0).optional(),
    /** Reference to ranking config to use */
    rankingConfigRef: z.string().optional(),
    /** Reference to eligibility config to use */
    eligibilityConfigRef: z.string().optional(),
})
    .strict();
/** Alias for clarity - ComparisonStepConfig is the same as ResultsStepConfig */
export const ComparisonStepConfigSchema = ResultsStepConfigSchema;
/** Detail section */
export const DetailSectionSchema = z
    .object({
    id: z.string().min(1),
    title: z.string().min(1).max(100),
    attributes: z.array(z.string()).min(1),
    collapsible: z.boolean().optional(),
    defaultCollapsed: z.boolean().optional(),
})
    .strict();
/** Details step configuration (Product Detail Step) */
export const DetailsStepConfigSchema = z
    .object({
    type: z.literal("details"),
    /** Sections to display on the product detail page */
    sections: z.array(DetailSectionSchema).min(1),
    showBackButton: z.boolean().optional(),
    showCompareButton: z.boolean().optional(),
    /** Show call-to-action button */
    showCtaButton: z.boolean().optional(),
    /** CTA button label */
    ctaButtonLabel: z.string().max(50).optional(),
    /** Reference to a template for rendering */
    templateRef: z.string().optional(),
    /** Enable product comparison feature */
    enableComparison: z.boolean().optional(),
    /** Maximum products to compare */
    maxCompareProducts: z.number().int().min(2).max(10).optional(),
})
    .strict();
/** Alias for clarity - ProductDetailStepConfig is the same as DetailsStepConfig */
export const ProductDetailStepConfigSchema = DetailsStepConfigSchema;
/** Click-out tracking configuration */
export const ClickOutTrackingConfigSchema = z
    .object({
    /** Enable click-out event tracking */
    enabled: z.boolean(),
    /** Include user inputs in tracking event */
    includeUserInputs: z.boolean().optional(),
    /** Include ranking position in tracking event */
    includeRankPosition: z.boolean().optional(),
    /** Include attribution data in tracking event */
    includeAttribution: z.boolean().optional(),
    /** Custom tracking fields to include */
    customFields: z.array(z.string()).optional(),
})
    .strict();
/** UTM parameter configuration */
export const UtmParamsConfigSchema = z
    .object({
    source: z.string().max(100).optional(),
    medium: z.string().max(100).optional(),
    campaign: z.string().max(100).optional(),
    term: z.string().max(100).optional(),
    content: z.string().max(100).optional(),
})
    .strict();
/** URL whitelist entry for security validation */
export const UrlWhitelistEntrySchema = z
    .object({
    /** Domain pattern (supports wildcards like *.example.com) */
    domain: z.string().min(1),
    /** Optional path prefix */
    pathPrefix: z.string().optional(),
    /** Whether this is a required domain (must match) or optional */
    required: z.boolean().optional(),
})
    .strict();
/** Click-out step configuration */
export const ClickOutStepConfigSchema = z
    .object({
    type: z.literal("click_out"),
    /** Legacy flag - use tracking.enabled instead */
    trackingEnabled: z.boolean(),
    /** Detailed tracking configuration */
    tracking: ClickOutTrackingConfigSchema.optional(),
    redirectDelay: z.number().int().min(0).max(10000).optional(),
    /** Static affiliate parameters to append */
    affiliateParams: z.record(z.string()).optional(),
    /** Dynamic affiliate parameters with template substitution */
    dynamicAffiliateParams: z.record(z.string()).optional(),
    /** UTM parameters for marketing attribution */
    utmParams: UtmParamsConfigSchema.optional(),
    /** URL whitelist for security - only these domains allowed */
    urlWhitelist: z.array(UrlWhitelistEntrySchema).optional(),
    confirmationMessage: z.string().max(255).optional(),
    openInNewTab: z.boolean().optional(),
    /** Show interstitial page before redirect */
    showInterstitial: z.boolean().optional(),
    /** Interstitial page message */
    interstitialMessage: z.string().max(500).optional(),
})
    .strict();
/** Union of all step configurations (base schemas for discriminated union) */
export const JourneyStepConfigSchema = z.discriminatedUnion("type", [
    FormStepConfigBaseSchema,
    ResultsStepConfigSchema,
    DetailsStepConfigSchema,
    ClickOutStepConfigSchema,
]);
// ============================================================================
// Journey Steps
// ============================================================================
/** Conditional next step */
export const ConditionalNextStepSchema = z
    .object({
    stepId: z.string().min(1),
    condition: EligibilityConditionSchema.optional(),
})
    .strict();
/** Journey step definition */
export const JourneyStepSchema = z
    .object({
    id: z.string().min(1),
    type: JourneyStepTypeSchema,
    config: JourneyStepConfigSchema,
    nextSteps: z.array(ConditionalNextStepSchema).optional(),
})
    .strict()
    .refine((data) => {
    // Validate that step type matches config type
    return data.type === data.config.type;
}, {
    message: "Step type must match config type",
    path: ["config", "type"],
});
// ============================================================================
// Step Transition Rules
// ============================================================================
/**
 * Valid step type transitions.
 * Defines which step types can transition to which other step types.
 * This enforces a logical flow in the journey.
 */
export const VALID_STEP_TRANSITIONS = {
    form: ["form", "results"], // Forms can lead to more forms or results
    results: ["details", "click_out"], // Results lead to product details or direct click-out
    details: ["click_out", "results"], // Details lead to click-out or back to results
    click_out: [], // Click-out is terminal - no forward transitions
};
/**
 * Validate that a step transition is allowed.
 */
export function isValidStepTransition(fromType, toType) {
    const allowedTransitions = VALID_STEP_TRANSITIONS[fromType];
    return allowedTransitions.includes(toType);
}
// ============================================================================
// Journey Configuration
// ============================================================================
/** Journey configuration options */
export const JourneyConfigOptionsSchema = z
    .object({
    /** Enforce strict step transition rules */
    enforceTransitionRules: z.boolean().optional(),
    /** Allow back navigation */
    allowBackNavigation: z.boolean().optional(),
    /** Allow journey restart */
    allowRestart: z.boolean().optional(),
    /** Session timeout in milliseconds */
    sessionTimeoutMs: z.number().int().min(0).optional(),
    /** Enable journey analytics */
    enableAnalytics: z.boolean().optional(),
})
    .strict();
/** Complete journey configuration */
export const JourneyConfigSchema = z
    .object({
    verticalKey: z.string().min(1),
    version: z.number().int().min(1),
    entryStep: z.string().min(1),
    steps: z.array(JourneyStepSchema).min(1),
    /** Optional journey configuration options */
    options: JourneyConfigOptionsSchema.optional(),
})
    .strict()
    .refine((data) => {
    // Validate entryStep exists in steps
    return data.steps.some((s) => s.id === data.entryStep);
}, {
    message: "entryStep must reference an existing step",
    path: ["entryStep"],
})
    .refine((data) => {
    // Validate all nextStep references exist
    const stepIds = new Set(data.steps.map((s) => s.id));
    for (const step of data.steps) {
        if (step.nextSteps) {
            for (const nextStep of step.nextSteps) {
                if (!stepIds.has(nextStep.stepId)) {
                    return false;
                }
            }
        }
    }
    return true;
}, {
    message: "All nextStep references must point to existing steps",
    path: ["steps"],
})
    .refine((data) => {
    // Validate step IDs are unique
    const stepIds = data.steps.map((s) => s.id);
    return new Set(stepIds).size === stepIds.length;
}, {
    message: "Step IDs must be unique",
    path: ["steps"],
})
    .refine((data) => {
    // Entry step must be a form step (journeys start with data collection)
    const entryStep = data.steps.find((s) => s.id === data.entryStep);
    return entryStep?.type === "form";
}, {
    message: "Entry step must be a form step",
    path: ["entryStep"],
})
    .refine((data) => {
    // Validate step transitions if enforceTransitionRules is enabled
    if (!data.options?.enforceTransitionRules) {
        return true; // Skip validation if not enforced
    }
    const stepMap = new Map(data.steps.map((s) => [s.id, s]));
    for (const step of data.steps) {
        if (!step.nextSteps) {
            continue;
        }
        for (const nextStep of step.nextSteps) {
            const targetStep = stepMap.get(nextStep.stepId);
            if (targetStep && !isValidStepTransition(step.type, targetStep.type)) {
                return false;
            }
        }
    }
    return true;
}, {
    message: "Invalid step transition detected. Check VALID_STEP_TRANSITIONS for allowed transitions.",
    path: ["steps"],
})
    .refine((data) => {
    // Ensure at least one terminal step exists (click_out with no next steps)
    return data.steps.some((s) => s.type === "click_out" && (!s.nextSteps || s.nextSteps.length === 0));
}, {
    message: "Journey must have at least one terminal click_out step",
    path: ["steps"],
})
    .refine((data) => {
    // Validate that all steps are reachable from entry
    const stepMap = new Map(data.steps.map((s) => [s.id, s]));
    const reachable = new Set();
    const queue = [data.entryStep];
    while (queue.length > 0) {
        const currentId = queue.shift();
        if (!currentId || reachable.has(currentId)) {
            continue;
        }
        reachable.add(currentId);
        const step = stepMap.get(currentId);
        if (step?.nextSteps) {
            for (const next of step.nextSteps) {
                if (!reachable.has(next.stepId)) {
                    queue.push(next.stepId);
                }
            }
        }
    }
    // All steps should be reachable
    return data.steps.every((s) => reachable.has(s.id));
}, {
    message: "All steps must be reachable from the entry step",
    path: ["steps"],
});
